/**
 * useScrollMemory — per-view window scroll memory.
 *
 * WHY. Every top-level tab renders into the SAME document, and the page
 * scrolls the window. Nothing ever reset that offset on a tab switch, so it
 * simply carried over: leaving the (very long) Utak tab near its end and
 * opening the (short) Építők tab parked the viewport far past the end of the
 * new content. Mobile browsers do not always clamp a shrinking document until
 * the next scroll gesture, so what the user got was a full screen of bare
 * background with no visible way back. Even when the browser did clamp, landing
 * mid-page in a tab you have never scrolled is disorienting.
 *
 * So each view keeps its OWN offset instead of sharing one. The offset is
 * persisted per gym in localStorage — same shape as `useExpanderState` /
 * `usePersistedPeriod`: one JSON blob, indexed by `"<scope>:<key>"` — so a tab
 * you left half-way down comes back half-way down, across reloads and across a
 * PWA restart. A view with nothing remembered opens at the top, which is also
 * what every first visit gets.
 *
 * Usage — pass the key of whatever view is currently on screen:
 *
 *   const restoring = useScrollMemory(gymId ? tab : null, gymId);  // null = off
 *
 * The returned ref holds `{active, target}` while a restore is in flight. It matters because
 * a restore is not one jump: this hook re-applies the offset every frame for
 * up to RESTORE_BUDGET_MS while the page fills in, and anything else that
 * reacts to scrolling would otherwise read all of that as the user moving.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { makeKeyedStore, scopedKey } from "../utils";

const store = makeKeyedStore("sendly-scroll");

// Tab content arrives asynchronously (Firestore snapshots, lazy chunks,
// images), so on the first frame the document is usually far too short to
// reach a remembered offset. Keep re-applying it while the page grows — but
// give up eventually, otherwise a tab whose content genuinely shrank (routes
// archived, a filter cleared) would keep yanking the page down under the user.
const RESTORE_BUDGET_MS = 1500;

// Trailing throttle for the scroll → localStorage write. `scroll` fires at
// frame rate; the offset only has to survive a reload, not every pixel.
const PERSIST_DELAY_MS = 300;

// How long our own scroll events stay disowned after a restore ends. Anything
// shorter risks a clamped intermediate offset (the page never grew enough)
// landing in storage as if the user had scrolled there.
const RELEASE_DELAY_MS = 150;

// A restore is a best guess about a page that is still filling in. The moment
// the user touches the page it stops being a guess — they take over.
const TAKEOVER_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];

function maxScroll() {
  const docH = Math.max(
    document.documentElement?.scrollHeight || 0,
    document.body?.scrollHeight || 0,
  );
  return Math.max(0, docH - window.innerHeight);
}

/**
 * Where a view WOULD be restored to, without waiting for the restore to start.
 *
 * The offset lives in localStorage, so it can be read synchronously at any
 * time — including during a render, before any effect has run. That matters
 * for chrome that has to be drawn in its final shape on the FIRST frame of a
 * tab switch: reading it off the hook's ref would be a frame too late, because
 * the ref is only filled in the layout phase, after the render that needs it.
 *
 * @param {string|null} key
 * @param {string|null} [scope]
 * @returns {number} the remembered offset, 0 when there is none
 */
export function peekScrollMemory(key, scope = null) {
  const fullKey = scopedKey(key, scope);
  if (!fullKey || typeof window === "undefined") return 0;
  return Math.max(0, Number(store.read()[fullKey]) || 0);
}

/**
 * @param {string|null} key — the view currently on screen (e.g. the tab key).
 *                            Null/empty disables the hook entirely.
 * @param {string|null} scope — optional prefix (e.g. gymId), so the same tab
 *                              remembers a separate offset per gym.
 */
export function useScrollMemory(key, scope = null) {
  const fullKey = scopedKey(key, scope);

  const keyRef = useRef(null);       // the view the tracked offset belongs to
  const yRef = useRef(0);            // last user-owned offset for keyRef
  // `{active, target}` rather than a bare boolean: chrome that reacts to
  // scrolling needs to know not just THAT we are moving the page but WHERE
  // to — see the return value at the bottom.
  const restoringRef = useRef({ active: false, target: null });
  const timerRef = useRef(null);

  const persist = useCallback((k, y) => {
    if (!k) return;
    const all = store.read();
    // Top is the default, so it is stored as "no entry" — that keeps the blob
    // from collecting a row for every tab the user merely passed through.
    if (y > 0) all[k] = Math.round(y); else delete all[k];
    store.write(all);
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    persist(keyRef.current, yRef.current);
  }, [persist]);

  // ── Track where the user is ──
  useEffect(() => {
    const onScroll = () => {
      // A modal is open: `ScrollLock` pins the body (position: fixed) and parks
      // the window at 0. That 0 is chrome state, not the user's place in the
      // list — ScrollLock puts the real offset back when the modal closes.
      if (document.body.style.position === "fixed") return;
      // Our own restore is not the user moving. Recording the clamped
      // intermediate offsets it walks through would erase the very offset it
      // is trying to reach.
      if (restoringRef.current.active) return;
      yRef.current = window.scrollY || 0;
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        persist(keyRef.current, yRef.current);
      }, PERSIST_DELAY_MS);
    };
    // `pagehide` covers the iOS PWA being swiped away and the tab being
    // discarded — neither runs an unmount, so a pending throttled write would
    // be lost with them.
    const onHide = () => flush();
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [persist, flush]);

  // ── Put the incoming view back where it was ──
  // useLayoutEffect (not useEffect): it runs after the new tab's DOM is in
  // place but BEFORE the browser paints, so the switch never flashes the
  // outgoing tab's offset.
  useLayoutEffect(() => {
    if (!fullKey) return undefined;
    // Leaving a view: bank where it was, before the DOM underneath changes and
    // the browser starts clamping the offset to the new content.
    if (keyRef.current && keyRef.current !== fullKey) flush();
    keyRef.current = fullKey;

    const target = Math.max(0, Number(store.read()[fullKey]) || 0);
    yRef.current = target;
    restoringRef.current = { active: true, target };

    let raf = 0;
    let releaseTimer = 0;
    const deadline = Date.now() + RESTORE_BUDGET_MS;


    const release = () => {
      clearTimeout(releaseTimer);
      releaseTimer = 0;
      restoringRef.current = { active: false, target: null };
      TAKEOVER_EVENTS.forEach(e => window.removeEventListener(e, onTakeover));
    };
    const stop = (fromUser) => {
      cancelAnimationFrame(raf);
      raf = 0;
      // `scrollTo` dispatches its scroll event a frame or two later, so
      // releasing the guard right away would let one of our own clamped
      // offsets overwrite the remembered one. The takeover listeners stay
      // armed across that gap, so a user who grabs the page right then is
      // still not mistaken for us — their scroll is the newer truth and must
      // be recorded.
      if (fromUser) release();
      else if (!releaseTimer) releaseTimer = setTimeout(release, RELEASE_DELAY_MS);
    };
    function onTakeover() { stop(true); }

    const step = () => {
      const y = Math.min(target, maxScroll());
      if (Math.round(window.scrollY || 0) !== y) window.scrollTo(0, y);
      // Reached it, or the page has run out of time to grow into it.
      if (y >= target || Date.now() > deadline) { stop(false); return; }
      raf = requestAnimationFrame(step);
    };

    TAKEOVER_EVENTS.forEach(e => window.addEventListener(e, onTakeover, { passive: true }));
    step();

    return () => { cancelAnimationFrame(raf); release(); };
  }, [fullKey, flush]);

  // Exposed so other scroll-driven chrome can tell OUR scrolling from the
  // user's — `{active, target}`. A ref rather than state on purpose: it is
  // read inside a scroll handler, where a re-render would be both pointless
  // and expensive. `target` is the offset this restore is heading for, known
  // synchronously here in the layout phase, so anything that has to look
  // right on the FIRST painted frame can adopt it instead of the offset the
  // page happens to be at.
  return restoringRef;
}
